import CustomToast from "../../utils/functions/CustomToast";
import HttpClient from "../../utils/functions/HttpClient";
import ServerEndpoints from "../../utils/constants/ServerEndpoints";
import Errors from "../../utils/constants/Errors";

const request = async (operation, { setApiError, setIsLoading } = {}) => {
  setApiError?.(false);

  try {
    return await operation();
  } catch (err) {
    setApiError?.(true);
    handleInvoiceRequestError(err);
    throw err;
  } finally {
    setIsLoading?.(false);
  }
};

const normalizeInvoiceQueryParams = (params = {}) => {
  const normalizedParams = { ...params };

  if (normalizedParams.pageSize != null && normalizedParams.size == null) {
    normalizedParams.size = normalizedParams.pageSize;
  }

  delete normalizedParams.pageSize;

  return normalizedParams;
};

const buildInvoicePayload = (invoice) => ({
  id: invoice.id ?? null,
  invoiceNumber: invoice.invoiceNumber ?? null,
  issuedDate: invoice.issuedDate,
  dueDate: invoice.dueDate,
  status: invoice.status,
  amountDue: Number(invoice.amountDue ?? 0),
  totalAmountPaid: Number(invoice.totalAmountPaid ?? invoice.amountPaid ?? 0),
  reservation: invoice.reservation ?? null,
  user: invoice.user ?? null,
});

export const getAllInvoices = async (setInvoices, setIsLoading, params, setApiError) => {
  const normalizedParams = normalizeInvoiceQueryParams(params);
  const response = await request(
    () => HttpClient.getAxiosClient().get(`${ServerEndpoints.INVOICES_ENDPOINT}`, { params: normalizedParams }),
    { setApiError, setIsLoading }
  );

  if (response.status === 200) {
    setInvoices(response.data);
  }
};

export const getInvoiceById = async (setInvoiceData, id, setIsLoading, setApiError) => {
  const response = await request(
    () => HttpClient.getAxiosClient().get(`${ServerEndpoints.INVOICES_ENDPOINT}/${id}`),
    { setApiError, setIsLoading }
  );

  if (response.status === 200) {
    setInvoiceData(response.data);
  }
};

export const getAllInvoicesByUserId = async (setInvoices, id, setIsLoading, setApiError, params) => {
  const normalizedParams = normalizeInvoiceQueryParams(params);
  const response = await request(
    () => HttpClient.getAxiosClient().get(`${ServerEndpoints.USERS_ENDPOINT}/${id}/invoices`, { params: normalizedParams }),
    { setApiError, setIsLoading }
  );

  if (response.status === 200) {
    setInvoices(response.data);
  }
};

export const createInvoice = async (invoice, setApiError, setState, setIsLoading) => {
  const payload = buildInvoicePayload(invoice);
  const response = await request(
    () => HttpClient.getAxiosClient().post(`${ServerEndpoints.INVOICES_ENDPOINT}`, payload),
    { setApiError, setIsLoading }
  );

  if (response.status === 201 || response.status === 200) {
    CustomToast("Invoice created successfully.", "success");
    setState?.((prev) => !prev);
  }

  return response.data;
};

export const updateInvoice = async (
  invoice,
  setApiError,
  setState,
  setIsLoading
) => {
  const payload = buildInvoicePayload(invoice);
  const response = await request(
    () => HttpClient.getAxiosClient().put(`${ServerEndpoints.INVOICES_ENDPOINT}/${invoice.id}`, payload),
    { setApiError, setIsLoading }
  );

  if (response.status === 200) {
    CustomToast("Invoice updated successfully.", "success");
    setState?.((prev) => !prev);
  }

  return response.data;
};

export const deleteInvoice = async (id, setState, setIsLoading, setApiError) => {
  const response = await request(
    () => HttpClient.getAxiosClient().delete(`${ServerEndpoints.INVOICES_ENDPOINT}/${id}`),
    { setApiError, setIsLoading }
  );

  if (response.status === 200 || response.status === 204) {
    CustomToast("Invoice deleted successfully.", "success");
    setState?.((prev) => !prev);
  }
};

export const deleteInvoices = async (ids, setState, setIsLoading, setApiError) => {
  const response = await request(
    () => HttpClient.getAxiosClient().delete(`${ServerEndpoints.INVOICES_ENDPOINT}`, { data: ids }),
    { setApiError, setIsLoading }
  );

  if (response.status === 200 || response.status === 204) {
    CustomToast("Invoices deleted successfully.", "success");
    setState?.((prev) => !prev);
  }
};

const getErrorStatus = (err) => err?.response?.status;

const getBackendErrorMessage = (err) => {
  const data = err?.response?.data;

  if (Array.isArray(data?.errors) && data.errors.length > 0) {
    return data.errors[0];
  }

  return data?.message || null;
};

const handleInvoiceRequestError = (err) => {
  const backendMessage = getBackendErrorMessage(err);

  switch (getErrorStatus(err)) {
    case 400:
      CustomToast(backendMessage || "Please review your data and try again.", "error");
      return;
    case 401:
      CustomToast("Please sign in to continue.", "error");
      return;
    case 403:
      CustomToast(backendMessage || "You are not allowed to perform this action.", "error");
      return;
    case 404:
      CustomToast(backendMessage || "The invoice could not be found.", "error");
      return;
    case 409:
      CustomToast(backendMessage || "Invoice already exists.", "error");
      return;
    case 503:
      CustomToast(Errors.SERVICE_UNAVALAIBLE_ERROR, "error");
      return;
    default:
      showGenericRequestError(err);
  }
};

const showGenericRequestError = (err) => {
  if (err?.response == null) {
    CustomToast(Errors.API_ERROR, "error");
    return;
  }

  const backendMessage = getBackendErrorMessage(err);
  CustomToast(backendMessage || Errors.API_ERROR, "error");
};
